import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { fetchHeatmapBbox } from '../api/heatmap';
import MapLegend from '../components/MapLegend';

type Operator = 'ORANGE' | 'SFR' | 'BOUYGUES TELECOM' | 'FREE MOBILE';
type Period = 'all' | '7d' | '24h' | '1h';

const PERIOD_MS: Record<Period, number | null> = {
  all: null,
  '7d': 7 * 24 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '1h': 60 * 60 * 1000,
};

function normalizeRsrp(v: number): number {
  return Math.max(0, Math.min(1, (v + 130) / 50));
}

function HeatmapMini({ operator, period, syncCenter, onCenterChange }: {
  operator: Operator;
  period: Period;
  syncCenter: { lng: number; lat: number; zoom: number };
  onCenterChange: (c: { lng: number; lat: number; zoom: number }) => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const loadedRef = useRef(false);
  const skipNextSyncRef = useRef(false);
  const fetchTimerRef = useRef<number | null>(null);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const queryAndUpdate = useCallback(async () => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const b = map.getBounds();
    const sinceMs = PERIOD_MS[period];
    setLoading(true);
    try {
      const data = await fetchHeatmapBbox({
        minLat: b.getSouth(), minLon: b.getWest(),
        maxLat: b.getNorth(), maxLon: b.getEast(),
        metric: 'rsrp',
        operator,
        since: sinceMs ? new Date(Date.now() - sinceMs).toISOString() : undefined,
      });
      setCount(data.count);
      (map.getSource('heatmap') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: data.points.map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
          properties: { value: normalizeRsrp(p.value) },
        })),
      });
    } catch (e) {
      console.warn('heatmap fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [operator, period]);

  const scheduleFetch = useCallback(() => {
    if (fetchTimerRef.current != null) window.clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = window.setTimeout(() => { queryAndUpdate(); }, 500);
  }, [queryAndUpdate]);

  useEffect(() => {
    if (!ref.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: {
        version: 8,
        sources: {
          'carto-dark': {
            type: 'raster',
            tiles: [
              'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
              'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
            ],
            tileSize: 256,
            attribution: '© OSM © CARTO',
          },
        },
        layers: [{ id: 'carto-dark', type: 'raster', source: 'carto-dark' }],
      },
      center: [syncCenter.lng, syncCenter.lat],
      zoom: syncCenter.zoom,
    });
    mapRef.current = map;
    map.on('load', () => {
      map.addSource('heatmap', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'heatmap-layer',
        type: 'heatmap',
        source: 'heatmap',
        maxzoom: 19,
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'value'], 0, 0, 1, 1],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 4, 8, 10, 22, 18, 42],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 4, 0.7, 18, 2],
          'heatmap-opacity': 0.78,
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(0,0,0,0)',
            0.2, '#ef4444',
            0.5, '#facc15',
            0.8, '#22c55e',
          ],
        },
      });
      loadedRef.current = true;
      queryAndUpdate();
    });

    const emitCenter = () => {
      const c = map.getCenter();
      onCenterChange({ lng: c.lng, lat: c.lat, zoom: map.getZoom() });
    };
    map.on('moveend', () => {
      emitCenter();
      scheduleFetch();
    });

    setTimeout(() => map.resize(), 100);
    setTimeout(() => map.resize(), 500);
    return () => { map.remove(); mapRef.current = null; loadedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync to external center changes (from the other map). Skip own emissions.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loadedRef.current) return;
    const c = map.getCenter();
    if (Math.abs(c.lng - syncCenter.lng) < 1e-6 && Math.abs(c.lat - syncCenter.lat) < 1e-6 && Math.abs(map.getZoom() - syncCenter.zoom) < 1e-3) return;
    skipNextSyncRef.current = true;
    map.easeTo({ center: [syncCenter.lng, syncCenter.lat], zoom: syncCenter.zoom, duration: 0 });
  }, [syncCenter]);

  // Re-fetch when filters change
  useEffect(() => {
    if (loadedRef.current) queryAndUpdate();
  }, [operator, period, queryAndUpdate]);

  return (
    <div style={{ position: 'absolute', inset: 0 }}>
      <div ref={ref} style={{ position: 'absolute', inset: 0 }} />
      <div className="text-[11px] text-slate-300 bg-slate-900/85 border border-slate-800 rounded-md px-2 py-1" style={{ position: 'absolute', top: 8, right: 8, zIndex: 5 }}>
        {loading ? 'Loading…' : `${count.toLocaleString()} cells`}
      </div>
      <MapLegend metric="rsrp" />
    </div>
  );
}

export default function ComparePage() {
  const [a, setA] = useState<Operator>('ORANGE');
  const [b, setB] = useState<Operator>('SFR');
  const [period, setPeriod] = useState<Period>('all');
  const [center, setCenter] = useState<{ lng: number; lat: number; zoom: number }>({ lng: 2.35, lat: 46.8, zoom: 5.5 });

  return (
    <div className="flex flex-col" style={{ position: 'absolute', inset: 0 }}>
      <div className="border-b border-slate-800 px-4 flex items-center gap-3 text-sm flex-wrap shrink-0" style={{ height: 48 }}>
        <span className="text-slate-400 text-xs uppercase tracking-wide font-bold">Compare</span>

        <select className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200" value={a} onChange={(e) => setA(e.target.value as Operator)}>
          <option value="ORANGE">Orange</option>
          <option value="SFR">SFR</option>
          <option value="BOUYGUES TELECOM">Bouygues Telecom</option>
          <option value="FREE MOBILE">Free Mobile</option>
        </select>

        <span className="text-slate-500 px-1">vs</span>

        <select className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200" value={b} onChange={(e) => setB(e.target.value as Operator)}>
          <option value="ORANGE">Orange</option>
          <option value="SFR">SFR</option>
          <option value="BOUYGUES TELECOM">Bouygues Telecom</option>
          <option value="FREE MOBILE">Free Mobile</option>
        </select>

        <span className="text-slate-500 px-2">·</span>

        <select className="bg-slate-900 border border-slate-700 rounded-md px-2 py-1 text-slate-200" value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
          <option value="all">All time</option>
          <option value="7d">Last 7 days</option>
          <option value="24h">Last 24 h</option>
          <option value="1h">Last hour</option>
        </select>

        <div className="flex-1" />
        <div className="text-[11px] text-slate-500">Synced pan/zoom · RSRP heatmap</div>
      </div>

      <div className="flex flex-col lg:flex-row" style={{ flex: '1 1 0', minHeight: 0 }}>
        <div className="lg:border-r border-slate-800" style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
          <div className="bg-slate-900/85 border border-slate-800 rounded-md px-2 py-1 text-xs font-bold text-emerald-400" style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>{a}</div>
          <HeatmapMini operator={a} period={period} syncCenter={center} onCenterChange={setCenter} />
        </div>
        <div className="border-t lg:border-t-0 border-slate-800" style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
          <div className="bg-slate-900/85 border border-slate-800 rounded-md px-2 py-1 text-xs font-bold text-blue-400" style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}>{b}</div>
          <HeatmapMini operator={b} period={period} syncCenter={center} onCenterChange={setCenter} />
        </div>
      </div>
    </div>
  );
}
